import { __awaiter } from "tslib";
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { capitalize, decorateTheme } from '@open-tender/utils';
import { ReducerType } from './types';
import OpenTenderAPI from '../services/api';
const initialState = {
    api: null,
    app: null,
    appTheme: null,
    brand: null,
    content: null,
    error: null,
    isApp: false,
    isBackgroundFetch: false,
    loading: 'idle',
    pages: null,
    retries: 0,
    settings: null,
    stylesheet: null,
    theme: null
};
export var ConfigActionType;
(function (ConfigActionType) {
    ConfigActionType["FetchConfig"] = "config/getConfig";
})(ConfigActionType || (ConfigActionType = {}));
export const fetchConfig = createAsyncThunk(ConfigActionType.FetchConfig, (options, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = new OpenTenderAPI(options);
        const response = yield api.getConfig();
        const { baseUrl, authUrl, clientId, brandId, callback, isApp } = options;
        const app = { baseUrl, authUrl, clientId, brandId };
        if (!app.clientId)
            app.clientId = response.clientId;
        if (!app.brandId)
            app.brandId = response.brand.brandId;
        if (callback)
            callback();
        return Object.assign(Object.assign({}, response), { isApp, app });
    }
    catch (err) {
        return rejectWithValue(err);
    }
}));
const configSlice = createSlice({
    name: ReducerType.Config,
    initialState: initialState,
    reducers: {
        resetConfig: () => initialState,
        resetRetries: state => {
            state.retries = 0;
        },
        incrementRetries: state => {
            state.retries = state.retries + 1;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchConfig.fulfilled, (state, action) => {
            const { app, isApp, brand, content, theme, settings, stylesheet, pages } = action.payload;
            state.app = app;
            state.brand = brand;
            state.content = content;
            state.theme = !isApp && theme ? theme : null;
            state.appTheme = isApp && theme ? decorateTheme(theme) : null;
            state.settings = settings;
            state.stylesheet = stylesheet;
            state.pages = pages;
            state.loading = 'idle';
            state.api = new OpenTenderAPI(app);
            state.error = null;
            state.retries = 0;
        })
            .addCase(fetchConfig.pending, (state, action) => {
            var _a;
            state.loading = 'pending';
            state.isBackgroundFetch = ((_a = action === null || action === void 0 ? void 0 : action.meta) === null || _a === void 0 ? void 0 : _a.arg) !== undefined ? true : false;
        })
            .addCase(fetchConfig.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetConfig, resetRetries, incrementRetries } = configSlice.actions;
export const selectConfig = (state) => state.config;
export const selectBrand = (state) => state.config.brand;
export const selectTheme = (state) => state.config.theme;
export const selectContent = (state) => state.config.content;
export const selectSettings = (state) => state.config.settings;
export const selectStylesheet = (state) => state.config.stylesheet;
export const selectPages = (state) => state.config.pages;
export const selectApi = (state) => state.config.api;
export const selectAccountConfig = (state) => { var _a; return (_a = state.config.content) === null || _a === void 0 ? void 0 : _a.account; };
export const selectCateringOnly = (state) => {
    const { orderTypes } = state.config.settings || {};
    return !!((orderTypes === null || orderTypes === void 0 ? void 0 : orderTypes.includes('CATERING')) && orderTypes.length === 1);
};
export const selectDisplaySettings = (state) => {
    var _a;
    const orderType = state.order.orderType || 'OLO';
    return (_a = state.config.settings) === null || _a === void 0 ? void 0 : _a.displaySettings[orderType];
};
export const selectConfigRetries = (state) => state.config.retries;
export const selectLightColor = (state) => state.config.theme ? state.config.theme.colors.light : '#ffffff';
export const selectOptIns = createSelector((state) => {
    const { accepts_marketing, order_notifications } = state.config.brand || {};
    return { accepts_marketing, order_notifications };
}, ({ accepts_marketing, order_notifications }) => {
    return { accepts_marketing, order_notifications };
});
export const selectShowLoyalty = createSelector((state) => {
    const { has_loyalty, tpls } = state.config.brand || {};
    return { has_loyalty, tpls };
}, ({ has_loyalty, tpls }) => {
    if (has_loyalty)
        return true;
    if (tpls === 'PUNCHH' || tpls === 'COMO')
        return true;
    return false;
});
export const selectFulfillment = (state) => { var _a; return (_a = state.config.brand) === null || _a === void 0 ? void 0 : _a.fulfillment; };
export const selectRecaptcha = (state) => { var _a; return (_a = state.config.settings) === null || _a === void 0 ? void 0 : _a.recaptcha; };
export const selectContentSection = (page) => (state) => state.config.content ? state.config.content[page] : undefined;
export const selectPage = (page) => (state) => state.config.pages ? state.config.pages[page] : undefined;
export const selectBottomTabsDisplayed = (state) => { var _a; return ((_a = state.config.content) === null || _a === void 0 ? void 0 : _a.bottomTabs.displayed) || []; };
export const selectBottomTabsTitles = createSelector((state) => {
    const { bottomTabs } = state.config.content || {};
    return bottomTabs;
}, bottomTabs => {
    if (!bottomTabs)
        return {};
    return Object.entries(bottomTabs).reduce((obj, [key, value]) => {
        if (key === 'displayed')
            return obj;
        return Object.assign(Object.assign({}, obj), { [key]: value.title });
    }, {});
});
export const selectSoldOutMsg = (state) => {
    var _a;
    const { soldOutMessage } = ((_a = state.config.content) === null || _a === void 0 ? void 0 : _a.menu) || {};
    return soldOutMessage || 'Sold out for day';
};
export const selectHeaderHeight = (state) => (isBrowser) => {
    if (!state.config.theme)
        return 0;
    const { navHeight, navHeightMobile } = state.config.theme.layout;
    const height = isBrowser ? navHeight : navHeightMobile;
    return parseInt(height.replace('rem', '')) * 10;
};
export const selectOutpostName = (state) => {
    var _a, _b, _c;
    return ((_a = state.config.settings) === null || _a === void 0 ? void 0 : _a.locationName.OUTPOST)
        ? capitalize((_c = (_b = state.config.settings) === null || _b === void 0 ? void 0 : _b.locationName.OUTPOST[0]) !== null && _c !== void 0 ? _c : '')
        : undefined;
};
export const selectHasCatering = (state) => {
    var _a;
    const { orderTypes } = state.config.settings || {};
    return (_a = orderTypes === null || orderTypes === void 0 ? void 0 : orderTypes.includes('CATERING')) !== null && _a !== void 0 ? _a : false;
};
export const selectCategoryType = (isMobile) => (state) => {
    const { categories } = state.config.theme || {};
    if (!categories)
        return 'LIST';
    const { desktop, mobile } = categories;
    return isMobile ? mobile.displayType : desktop.displayType;
};
export const configReducer = configSlice.reducer;
